import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faPlus, faCheckCircle, faSignature } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { COMPLIANCE_NAV } from './nav';

interface Policy {
    id: number; title: string; version: string; body: string; requires_attestation: boolean;
    effective_date: string | null; review_due: string | null;
    attested_count: number; eligible_count: number; self_attested: boolean;
}

const PolicyAttestation: React.FC = () => {
    const { api } = useInternalApi();
    const [policies, setPolicies] = useState<Policy[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ title: '', version: '1.0', body: '', requires_attestation: true, effective_date: '' });
    const [viewing, setViewing] = useState<Policy | null>(null);

    useEffect(() => { document.title = 'Policies & Attestation - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('compliance-api/policies/');
            if (res.data?.status === 'success') setPolicies(res.data.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const submitCreate = async () => {
        if (!form.title.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Title is required.' });
            return;
        }
        try {
            await api.post('compliance-api/policies/', form);
            setShowCreate(false);
            setForm({ title: '', version: '1.0', body: '', requires_attestation: true, effective_date: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not publish policy.' });
        }
    };

    const attest = async (p: Policy) => {
        const confirm = await Swal.fire({
            title: `Attest to "${p.title}" v${p.version}?`,
            text: 'This records that you have read and accepted this policy version.',
            icon: 'question', showCancelButton: true, confirmButtonText: 'I attest',
        });
        if (!confirm.isConfirmed) return;
        try {
            await api.post(`compliance-api/policies/${p.id}/attest/`, {});
            Swal.fire({ icon: 'success', title: 'Attestation recorded', timer: 1400, showConfirmButton: false });
            setViewing(null);
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not record attestation.' });
        }
    };

    return (
        <InternalShell activeDashboard="compliance_center" subNav={COMPLIANCE_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faFileContract} className="me-2 text-primary" />Policies & Attestation</h2>
                    <p className="text-muted mb-0">Published policies and who has attested to each version</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Publish Policy</button>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : policies.length === 0 ? (
                        <div className="text-center py-5 text-muted">No policies published yet.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Policy</th><th>Effective</th><th>Attestation Coverage</th><th>You</th><th></th></tr></thead>
                            <tbody>
                                {policies.map(p => {
                                    const pct = p.eligible_count ? Math.round((p.attested_count / p.eligible_count) * 100) : 0;
                                    return (
                                        <tr key={p.id}>
                                            <td style={{ cursor: 'pointer' }} onClick={() => setViewing(p)}>
                                                <strong>{p.title}</strong> <span className="badge bg-light text-dark">v{p.version}</span>
                                                {!p.requires_attestation && <div className="small text-muted">Attestation not required</div>}
                                            </td>
                                            <td className="small">{p.effective_date || '—'}</td>
                                            <td style={{ minWidth: 160 }}>
                                                {p.requires_attestation ? (
                                                    <>
                                                        <div className="progress" style={{ height: 6 }}>
                                                            <div className={`progress-bar ${pct === 100 ? 'bg-success' : 'bg-warning'}`} style={{ width: `${pct}%` }} />
                                                        </div>
                                                        <span className="small text-muted">{p.attested_count} of {p.eligible_count} active staff</span>
                                                    </>
                                                ) : <span className="text-muted small">—</span>}
                                            </td>
                                            <td>
                                                {p.self_attested
                                                    ? <span className="badge bg-success"><FontAwesomeIcon icon={faCheckCircle} className="me-1" />Attested</span>
                                                    : p.requires_attestation ? <span className="badge bg-warning">Outstanding</span> : <span className="text-muted">—</span>}
                                            </td>
                                            <td className="text-end">
                                                {p.requires_attestation && !p.self_attested && (
                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => attest(p)}>
                                                        <FontAwesomeIcon icon={faSignature} className="me-1" />Attest
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Publish Policy</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3 mb-3">
                                    <div className="col-8">
                                        <label className="form-label">Title</label>
                                        <input className="form-control" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
                                    </div>
                                    <div className="col-4">
                                        <label className="form-label">Version</label>
                                        <input className="form-control" value={form.version} onChange={(e) => setForm(f => ({ ...f, version: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Policy text</label>
                                    <textarea className="form-control" rows={8} value={form.body} onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))} />
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <label className="form-label">Effective date</label>
                                        <input type="date" className="form-control" value={form.effective_date} onChange={(e) => setForm(f => ({ ...f, effective_date: e.target.value }))} />
                                    </div>
                                    <div className="col-6 d-flex align-items-end">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id="reqAtt" checked={form.requires_attestation}
                                                onChange={(e) => setForm(f => ({ ...f, requires_attestation: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="reqAtt">Requires attestation from all internal staff</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-text mt-2">Publishing a new version resets attestation — staff must attest to each version separately.</div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitCreate}>Publish</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{viewing.title} <span className="badge bg-light text-dark">v{viewing.version}</span></h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="small text-muted mb-3">
                                    Effective {viewing.effective_date || '—'} · {viewing.attested_count} of {viewing.eligible_count} active staff attested
                                </div>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{viewing.body || <span className="text-muted">No policy text recorded.</span>}</div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                                {viewing.requires_attestation && !viewing.self_attested && (
                                    <button className="btn btn-primary" onClick={() => attest(viewing)}>
                                        <FontAwesomeIcon icon={faSignature} className="me-1" />I attest to this policy
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default PolicyAttestation;
